import type { ReactNode } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useViewNavigation } from '../../view-atoms'
import { useSidepanelSurface } from '../../profile/use-sidepanel-profile'
import { SidepanelHeaderBackButton } from './sidepanel-header-back-button'
import { SidepanelHeaderForwardButton } from './sidepanel-header-forward-button'

const navButtonVariants: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 },
}

const navButtonTransition: Transition = {
  type: 'spring',
  duration: 0.2,
  bounce: 0,
}

interface ViewChromeNavProps {
  /** Lock the overlay, or pin a popout on top — same control, different click. */
  pin: ReactNode
  className?: string
}

export function ViewChromeNav({ pin, className }: ViewChromeNavProps) {
  const { canGoBack, canGoForward } = useViewNavigation()
  const isHomeNavigation = useSidepanelSurface('navigation') === 'home'
  // Home navigation keeps no forward history, so Home is the only place it hides.
  const hasNavHistory = isHomeNavigation ? canGoBack : canGoBack || canGoForward

  return (
    <div className={twMerge('flex items-center gap-1 overflow-hidden', className)}>
      {pin}
      <AnimatePresence mode="popLayout">
        {hasNavHistory && (
          <motion.div
            key={isHomeNavigation ? 'home-nav' : 'history-nav'}
            className="flex items-center gap-1"
            variants={navButtonVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={navButtonTransition}
          >
            {isHomeNavigation ? (
              <SidepanelHeaderBackButton standalone />
            ) : (
              <>
                <SidepanelHeaderBackButton />
                <SidepanelHeaderForwardButton />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
